import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  widthLabelToCol,
  heightLabelToRow,
  typeSupportsAggregation,
  getAvailableMetrics,
} from '../../schemas';
import type { AddWidgetFormInput } from '../../schemas';
import type {
  WidgetDef,
  WidgetScope,
  WidgetType,
  WidgetConfigScope,
  WidgetConfigQuery,
  WidgetLayoutBreakpoint,
  GroupByDimension,
} from '../../types';
import { useCreateWidget, useUpdateWidget } from '../../hooks/useDashboardMutations';
import {
  serializeFilterGroup,
  parseFilterGroup,
  pruneFilterGroup,
} from '../../utils/filterSerializer';
import { StepTypeSelector } from './StepTypeSelector';
import { StepConfig } from './StepConfig';

type Step = 'type' | 'config';

interface AddWidgetModalProps {
  open: boolean;
  scope: WidgetScope;
  editWidget?: WidgetDef | null;
  existingWidgets?: WidgetDef[];
  onClose: () => void;
}

const GRID_COLS = 12;
const EMPTY_WIDGETS: WidgetDef[] = [];

// First free top-left slot for a w×h block on the 12-col grid (left-to-right,
// top-to-bottom) so new widgets fill gaps beside existing ones instead of stacking.
function findFreeSlot(
  occupied: WidgetLayoutBreakpoint[],
  w: number,
  h: number,
): { x: number; y: number } {
  const collides = (x: number, y: number) =>
    occupied.some((it) => x < it.x + it.w && x + w > it.x && y < it.y + it.h && y + h > it.y);
  for (let y = 0; ; y++) {
    for (let x = 0; x + w <= GRID_COLS; x++) {
      if (!collides(x, y)) return { x, y };
    }
  }
}

const AGG_FUNCTIONS = ['count', 'sum', 'avg', 'min', 'max', 'percent'] as const;
const AGG_CONDITIONS = ['completed', 'archived', 'has_assignee'] as const;
const DATE_FIELDS = ['createdAt', 'dueDate', 'startDate'] as const;
// `custom` is UI-only — the backend has no such preset (see widgetToFormDefaults
// and handleSubmit, which translate it to/from explicit start/end dates).
const DATE_PRESETS = ['this_week', 'this_month', 'last_7', 'last_30'] as const;
const MAX_ITEMS = ['10', '20', '50', '100'] as const;
const DEFAULT_MAX_ITEMS = 50;
// Sum/Avg/Min/Max are computed over this field only, matched by exact name.
const AGGREGATION_FIELD = 'EstimatedHours' as const;
const FIELD_AGGREGATIONS = ['sum', 'avg', 'min', 'max'];

// Coerce a backend value to a member of the form enum, falling back when the
// stored value is outside the allowed set — prevents silent validation failures
// on fields that may not be rendered (e.g. aggregation condition).
function pick<T extends readonly string[]>(
  allowed: T,
  value: unknown,
  fallback?: T[number],
): T[number] | undefined {
  if (typeof value !== 'string') return fallback;
  const match = allowed.find((a) => a.toLowerCase() === value.toLowerCase());
  return (match as T[number] | undefined) ?? fallback;
}

function widgetToFormDefaults(widget: WidgetDef): Partial<AddWidgetFormInput> {
  const query = widget.config.query;
  const preset = pick(DATE_PRESETS, query?.dateRangePreset);
  // No preset but explicit dates stored → the user picked a custom range.
  const hasCustomRange = !preset && !!(query?.dateRangeStart || query?.dateRangeEnd);
  const maxItems = widget.config.options?.maxItems;
  return {
    type: widget.type,
    title: widget.title,
    widthLabel:
      widget.layout.desktop.w === 3
        ? '1/4'
        : widget.layout.desktop.w === 4
          ? '1/3'
          : widget.layout.desktop.w === 6
            ? '1/2'
            : widget.layout.desktop.w === 8
              ? '2/3'
              : 'Completo',
    heightLabel:
      widget.layout.desktop.h === 2
        ? 'Compacto'
        : widget.layout.desktop.h === 3
          ? 'Normal'
          : widget.layout.desktop.h === 4
            ? 'Alto'
            : 'Extra',
    metric: widget.config.groupBy ?? '',
    aggregationFunction: pick(AGG_FUNCTIONS, widget.config.aggregation?.function, 'count'),
    aggregationCondition: pick(AGG_CONDITIONS, widget.config.aggregation?.condition, 'completed'),
    // Coerce null -> undefined: the schema fields are optional strings, and
    // z.optional() rejects null (the backend returns null for unset scope).
    scopeProjectId: widget.config.scope?.projectId ?? undefined,
    scopeBoardId: widget.config.scope?.boardId ?? undefined,
    archivedOnly: query?.archivedOnly ?? false,
    maxItems: pick(MAX_ITEMS, maxItems === undefined ? undefined : String(maxItems)),
    dateRangeField: pick(DATE_FIELDS, query?.dateRangeField),
    dateRangePreset: hasCustomRange ? 'custom' : preset,
    dateRangeStart: query?.dateRangeStart ?? undefined,
    dateRangeEnd: query?.dateRangeEnd ?? undefined,
    filters: parseFilterGroup(query?.filtersJson),
  };
}

export function AddWidgetModal({
  open,
  scope,
  editWidget,
  existingWidgets = EMPTY_WIDGETS,
  onClose,
}: AddWidgetModalProps) {
  const { t } = useTranslation('dashboard');
  const isEdit = !!editWidget;

  const [prevOpen, setPrevOpen] = useState(open);
  const [step, setStep] = useState<Step>(isEdit ? 'config' : 'type');
  const [selectedType, setSelectedType] = useState<WidgetType | null>(editWidget?.type ?? null);

  // Derived state pattern: reset step and type when the modal reopens.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setStep(editWidget ? 'config' : 'type');
      setSelectedType(editWidget?.type ?? null);
    }
  }

  const createMutation = useCreateWidget(scope);
  const updateMutation = useUpdateWidget(scope, editWidget?.id ?? '');

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleTypeSelect = (type: WidgetType) => {
    setSelectedType(type);
    setStep('config');
  };

  const handleBack = () => {
    if (isEdit) {
      onClose();
    } else {
      setStep('type');
    }
  };

  const handleSubmit = (data: AddWidgetFormInput) => {
    const w = widthLabelToCol(data.widthLabel);
    const h = heightLabelToRow(data.heightLabel);
    const tabletW = Math.min(w, 6) as WidgetLayoutBreakpoint['w'];

    // Edit: keep the widget's current position, only change the size. Create:
    // auto-place in the first free slot so it fills gaps beside existing widgets.
    let desktop: WidgetLayoutBreakpoint;
    if (isEdit && editWidget) {
      const d = editWidget.layout.desktop;
      desktop = { x: d.x, y: d.y, w, h };
    } else {
      const slot = findFreeSlot(
        existingWidgets.map((widget) => widget.layout.desktop),
        w,
        h,
      );
      desktop = { x: slot.x, y: slot.y, w, h };
    }
    const layout = {
      desktop,
      // tablet/mobile stack in a single column; the responsive grid compacts them.
      tablet: { x: 0, y: desktop.y, w: tabletW, h },
      mobile: { x: 0, y: desktop.y, w: 12, h },
    };

    // Aggregation applies to every shape except Table. Sum/Avg/Min/Max require
    // a field (computed over estimated hours, the only field the backend
    // supports); Percent requires a condition.
    const fn = data.aggregationFunction ?? 'count';
    const aggregation = typeSupportsAggregation(data.type)
      ? {
          function: fn,
          ...(FIELD_AGGREGATIONS.includes(fn) ? { field: AGGREGATION_FIELD } : {}),
          ...(fn === 'percent' ? { condition: data.aggregationCondition ?? 'completed' } : {}),
        }
      : undefined;

    // Scope — tenant dashboards can pin a project; both can pin a board. On a
    // project dashboard the backend resolves the project from the route.
    const widgetScope: WidgetConfigScope = {};
    if (scope.type === 'tenant' && data.scopeProjectId) {
      widgetScope.projectId = data.scopeProjectId;
    }
    if (data.scopeBoardId) {
      widgetScope.boardId = data.scopeBoardId;
    }

    // Query — date range, archived flag and serialized filters (FilterGroup).
    const query: WidgetConfigQuery = {};
    if (data.archivedOnly) {
      query.archivedOnly = true;
    }
    if (data.dateRangePreset) {
      query.dateRangeField = data.dateRangeField ?? 'createdAt';
      if (data.dateRangePreset === 'custom') {
        // The backend has no `custom` preset — a custom range is an absent
        // preset plus explicit dates, not an unrecognized preset string.
        if (data.dateRangeStart) query.dateRangeStart = data.dateRangeStart;
        if (data.dateRangeEnd) query.dateRangeEnd = data.dateRangeEnd;
      } else {
        query.dateRangePreset = data.dateRangePreset;
      }
    }
    const filters = data.filters ? pruneFilterGroup(data.filters) : undefined;
    if (filters) {
      query.filtersJson = serializeFilterGroup(filters);
    }

    // groupBy is meaningless for Counter/Table (the backend ignores it) — only
    // send it for the shapes that actually bucket.
    const groupBy = getAvailableMetrics(data.type, scope.type).length > 0 ? data.metric : '';

    const configFields = {
      ...(groupBy ? { groupBy: groupBy as GroupByDimension } : {}),
      ...(aggregation ? { aggregation } : {}),
      ...(Object.keys(widgetScope).length > 0 ? { scope: widgetScope } : {}),
      ...(Object.keys(query).length > 0 ? { query } : {}),
      options: {
        showLegend: true,
        maxItems: data.maxItems ? Number(data.maxItems) : DEFAULT_MAX_ITEMS,
      },
    };

    if (isEdit && editWidget) {
      updateMutation.mutate(
        {
          title: data.title,
          desktop: layout.desktop,
          tablet: layout.tablet,
          mobile: layout.mobile,
          ...configFields,
        },
        { onSuccess: onClose },
      );
    } else {
      createMutation.mutate(
        {
          type: data.type,
          title: data.title,
          desktop: layout.desktop,
          tablet: layout.tablet,
          mobile: layout.mobile,
          ...configFields,
        },
        { onSuccess: onClose },
      );
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] w-full sm:max-w-4xl flex-col overflow-hidden p-0"
        data-testid="dashboard-add-widget-modal"
      >
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>{isEdit ? t('addWidget.editTitle') : t('addWidget.title')}</DialogTitle>
          <DialogDescription>{t('addWidget.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left: type selector */}
          {!isEdit && (
            <div className="w-40 shrink-0 overflow-y-auto border-r px-3 py-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('addWidget.typeStep.label')}
              </p>
              <StepTypeSelector selected={selectedType} onSelect={handleTypeSelect} />
            </div>
          )}

          {/* Right: config form */}
          {step === 'config' && selectedType && (
            <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">
              <StepConfig
                type={selectedType}
                scopeType={scope.type}
                dashboardProjectId={scope.projectId}
                dashboardBoardId={scope.boardId}
                defaultValues={isEdit && editWidget ? widgetToFormDefaults(editWidget) : undefined}
                isLoading={isLoading}
                onSubmit={handleSubmit}
                onBack={handleBack}
                isEdit={isEdit}
              />
            </div>
          )}

          {/* Empty right panel before type selection */}
          {step === 'type' && (
            <div className="hidden flex-1 items-center justify-center text-sm text-muted-foreground md:flex">
              {t('addWidget.typeStep.label')}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
